import { Copy, ExternalLink, ShieldCheck, Check } from "lucide-react";
import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { DONATION, SITE } from "@/lib/data";
import { Reveal, SectionHeading } from "@/components/Reveal";

export const Donation = () => {
  const [copied, setCopied] = useState(false);
  const { bank } = DONATION;

  const copyAccount = async () => {
    try {
      await navigator.clipboard.writeText(bank.number.replace(/\s/g, ""));
      setCopied(true);
      toast.success("Nomor rekening disalin", { description: `${bank.name} · a.n. ${bank.holder}` });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Tidak dapat menyalin otomatis", { description: "Silakan salin nomor rekening secara manual." });
    }
  };

  return (
    <section id="donasi" className="dark-surface relative overflow-hidden bg-teal py-28 text-sand-50 lg:py-36" data-testid="donation-section">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -bottom-40 -right-20 h-[34rem] w-[34rem] rounded-full bg-cyan/20 blur-3xl" />
        <div className="absolute -left-20 top-10 h-[24rem] w-[24rem] rounded-full bg-gold/10 blur-3xl" />
      </div>

      <div className="relative mx-auto grid max-w-[1400px] items-start gap-14 px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-6">
          <SectionHeading
            dark
            eyebrow="Donasi & Infaq"
            title={
              <>
                Salurkan infaq terbaik, <em>rawat</em> rumah Allah di tepi laut.
              </>
            }
            description="Bangunan telah berdiri, namun menara, tata suara, dan pencahayaan masih dalam penyempurnaan. Setiap rupiah membantu masjid ini terus hidup—dengan ilmu, kegiatan sosial, dan pemberdayaan pesisir."
          />
          <ul className="mt-12 space-y-6">
            {DONATION.uses.map((u, i) => (
              <Reveal key={u.title} delay={0.1 + i * 0.08}>
                <li className="flex gap-5 border-t border-sand-50/15 pt-6">
                  <span className="font-grotesk text-2xl font-extrabold text-gold">0{i + 1}</span>
                  <span>
                    <span className="block font-display text-2xl">{u.title}</span>
                    <span className="mt-1 block text-sm text-sand-50/70">{u.desc}</span>
                  </span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>

        <Reveal delay={0.2} className="lg:col-span-6">
          <div className="rounded-[2.5rem] bg-sand-50 p-7 text-navy shadow-2xl shadow-navy/30 sm:p-10" data-testid="donation-card">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow text-teal">Transfer Bank</p>
                <p className="mt-2 font-display text-2xl">{bank.name}</p>
              </div>
              <span className="grid h-11 w-11 place-items-center rounded-full bg-navy text-gold">
                <ShieldCheck className="h-5 w-5" />
              </span>
            </div>

            <div className="mt-6 rounded-3xl border border-navy/10 bg-white p-6">
              <p className="text-xs text-slate-500">Nomor rekening</p>
              <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
                <span className="font-grotesk text-3xl font-extrabold tabular-nums tracking-wide sm:text-4xl" data-testid="donation-account-number">
                  {bank.number}
                </span>
                <button onClick={copyAccount} className="btn-primary !px-5 !py-3" data-testid="copy-account-button">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? "Tersalin" : "Salin"}
                </button>
              </div>
              <p className="mt-3 text-sm text-slate-600">
                a.n. <span className="font-semibold text-navy">{bank.holder}</span>
              </p>
              {bank.isSample && (
                <p className="mt-3 rounded-xl bg-gold/15 px-3 py-2 text-xs text-navy/80" data-testid="donation-sample-note">
                  Nomor rekening contoh—mohon dikonfirmasi & diperbarui oleh pengurus DKM sebelum dipublikasikan.
                </p>
              )}
            </div>

            <div className="mt-6 grid gap-6 rounded-3xl border border-navy/10 bg-white p-6 sm:grid-cols-[auto,1fr] sm:items-center">
              <div className="mx-auto rounded-2xl border border-navy/10 bg-sand-50 p-3" data-testid="donation-qr">
                <QRCodeSVG value={SITE.kitabisa} size={148} bgColor="transparent" fgColor="#0B2545" level="M" />
              </div>
              <div>
                <p className="eyebrow text-teal">Scan untuk berdonasi</p>
                <p className="mt-2 font-display text-2xl leading-tight">Donasi daring via Kitabisa</p>
                <p className="mt-2 text-sm text-slate-600">
                  Kampanye resmi <span className="font-semibold text-navy">Masjid Pantai Nusantara</span> (identitas terverifikasi). Tersedia GoPay,
                  BCA, BNI, BRI, Mandiri, kartu kredit.
                </p>
                <a href={SITE.kitabisa} target="_blank" rel="noopener noreferrer" className="btn-accent mt-5 !px-5 !py-3" data-testid="kitabisa-link">
                  Donasi via Kitabisa <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>

            <p className="mt-6 text-center text-xs text-slate-500">
              Konfirmasi transfer dapat dikirim melalui formulir kontak atau Instagram {SITE.instagramHandle}.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
